import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import NetworkBackground from '../components/NetworkBackground';
import Toast from '../components/Toast';
import styles from './EmployeeRegister.module.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function EmployeeRegister() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', password: '' });

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    if (!token) {
      showToast('No invite token found in the URL.', 'error');
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/invites/validate?token=${token}`)
      .then((res) => {
        if (!res.ok) throw new Error('Invalid invite');
        return res.json();
      })
      .then((data) => { setInviteData(data); setLoading(false); })
      .catch(() => { showToast('Invalid or expired invitation link.', 'error'); setLoading(false); });
  }, [token]);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/employees/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, firstName: formData.firstName, lastName: formData.lastName, password: formData.password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Registration failed');
      }

      showToast('Account created! Redirecting to login…', 'success');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      showToast(err.message || 'Registration failed.', 'error');
    }
  };

  if (loading) {
    return (
      <div className={styles.pageWrapper}>
        <NetworkBackground />
        <div className={styles.card}>
          <div className={styles.logoContainer}>
            <img src="/logo.png" alt="Nodel logo" className={styles.logo} />
            <p className={styles.slogan}>Organize. Schedule. Deliver.</p>
          </div>
          <div className={styles.spinner} />
          <p style={{ color: '#6b7280', margin: 0 }}>Loading your invitation…</p>
        </div>
      </div>
    );
  }

  if (!inviteData) {
    return (
      <div className={styles.pageWrapper}>
        <NetworkBackground />
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className={styles.card}>
          <div className={styles.logoContainer}>
            <img src="/logo.png" alt="Nodel logo" className={styles.logo} />
            <p className={styles.slogan}>Organize. Schedule. Deliver.</p>
          </div>
          <div className={styles.iconWrapper}>
            <svg className={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className={styles.errorTitle}>Invalid Invitation</h1>
          <p className={styles.errorBody}>This invitation link is invalid or has expired. Please ask your administrator for a new one.</p>
          <Link to="/login" className={styles.backLink}>Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <NetworkBackground />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className={styles.card}>
        <div className={styles.logoContainer}>
          <img src="/logo.png" alt="Nodel logo" className={styles.logo} />
          <p className={styles.slogan}>Organize. Schedule. Deliver.</p>
          <p className={styles.subtitle}>Join {inviteData.companyName || 'your team'}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <input
            className={styles.input}
            type="email"
            value={inviteData.email}
            disabled
            placeholder="Email"
          />

          <div className={styles.formRow}>
            <input
              className={styles.input}
              name="firstName"
              type="text"
              placeholder="First name"
              required
              value={formData.firstName}
              onChange={handleChange}
            />
            <input
              className={styles.input}
              name="lastName"
              type="text"
              placeholder="Last name"
              required
              value={formData.lastName}
              onChange={handleChange}
            />
          </div>

          <input
            className={styles.input}
            name="password"
            type="password"
            placeholder="Password"
            required
            minLength={8}
            value={formData.password}
            onChange={handleChange}
          />
          <p className={styles.hint}>Minimum 8 characters</p>

          <button type="submit" className={styles.button}>
            Create Account
          </button>
        </form>

        <div className={styles.divider} />
        <div className={styles.footerText}>
          Already have an account? <Link to="/login" style={{ color: '#185FA5', textDecoration: 'none', fontWeight: 500 }}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}
